import type { PlayCategory } from '@frontend/core'
import type { ReportingSport } from '@frontend/core/lib/domains/reporting/types'
import { isGridironSport } from '@frontend/core/lib/domains/reporting/types'
import type { DrillRegion, PitchInput } from '@frontend/pitch'
import { YARD_M } from '@frontend/pitch'
import { ErrorBoundary, PassChart } from '@frontend/react'
import { Button, Card } from '@frontend/ui'
import { downloadFromUrl } from '@frontend/utils'
import { Download } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { PitchMap } from '../../../components/pitch-map'
import { PitchMetricsRow } from '../../../components/pitch-metrics-row'
import type { PitchViewMode } from '../../../components/pitch-view-toggle'
import { PitchViewToggle } from '../../../components/pitch-view-toggle'
import { PlayCategorySelection } from '../../../components/play-category-selection'
import type { PitchExportPlayer } from '../../../lib/pitch-export'
import type { PitchArrow, PitchPoint } from '../../../lib/play-visualization'
import type { useReportingState } from '../../../lib/reporting-state'
import type { PlayCategoryDef } from '../../../lib/sport-config'

// Gridlines at 5yd intervals. The pass chart intentionally omits yardage
// labels — the numbers would imply a line-of-scrimmage frame, which doesn't
// apply here (every throw's start X is collapsed onto a shared zero line).
const gridSpacingMeters = 5 * YARD_M

// ---------------------------------------------------------------------------
// PitchCard
// ---------------------------------------------------------------------------

type PitchCardProps = {
  categories: PlayCategoryDef[]
  isCategorySelected: (cat: PlayCategory) => boolean
  toggleCategory: (cat: PlayCategory) => void
  metrics: { title: string; value: string | number }[]
  points: PitchPoint[]
  arrows: PitchArrow[]
  /** Unfiltered arrow set — used to stabilise the pass-chart bounds
   *  so toggling category pills doesn't rescale the view. */
  allArrows: PitchArrow[]
  selectedElementId: string | null
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  onElementClick: (id: string) => void
  pitch: PitchInput
  drillRegion?: DrillRegion
  sport: ReturnType<typeof useReportingState>['sport']
  /** Metadata driving the PNG export composite. */
  exportPlayer: PitchExportPlayer | null
  exportSessionName: string
  exportSessionDate: string
  /** Heading text used on the full-pitch export — drill name if one is
   *  picked, "Full Session" otherwise. Ignored on the pass-chart view. */
  exportDrillName: string
  /** Shown in place of the pitch/pass-chart when there's no event data. */
  emptyMessage?: string
}

export const PitchCard = ({
  categories,
  isCategorySelected,
  toggleCategory,
  metrics,
  points,
  arrows,
  allArrows,
  selectedElementId,
  canvasRef,
  onElementClick,
  pitch,
  drillRegion,
  sport,
  exportPlayer,
  exportSessionName,
  exportSessionDate,
  exportDrillName,
  emptyMessage
}: PitchCardProps) => {
  const supportsPassChart = isGridironSport(sport)

  const [viewMode, setViewMode] = useState<PitchViewMode>(
    supportsPassChart ? 'pass-chart' : 'full'
  )
  const [exporting, setExporting] = useState(false)

  // Tallies for the export legend + stat cells, reflecting only what's
  // currently visible so the PNG snapshot matches what the user sees.
  const exportTallies = useMemo(() => {
    const counts = new Map<
      string,
      { total: number; success: number; def: PlayCategoryDef }
    >()

    for (const def of categories) {
      counts.set(def.category, { total: 0, success: 0, def })
    }

    const tally = (cat: string, isSuccess: boolean) => {
      const entry = counts.get(cat)
      if (!entry) return
      entry.total += 1
      if (isSuccess) entry.success += 1
    }

    for (const a of arrows) tally(a.category, a.isSuccess)
    for (const p of points) tally(p.category, p.isSuccess)

    return Array.from(counts.values()).map((e) => ({
      def: e.def,
      count: e.total,
      successes: e.success
    }))
  }, [categories, arrows, points])

  const handleExportPng = useCallback(async () => {
    if (!canvasRef.current || !exportPlayer) return
    setExporting(true)
    try {
      const { renderPitchExportPng } = await import('../../../lib/pitch-export')
      const url = await renderPitchExportPng({
        source: canvasRef.current,
        viewMode,
        heading: exportDrillName,
        player: exportPlayer,
        sessionName: exportSessionName,
        sessionDate: exportSessionDate,
        categories: exportTallies.filter((t) => t.count > 0),
        // Export selected action metrics so the PNG matches what's on screen
        selectedMetrics: selectedElementId ? metrics : undefined
      })
      const safeName = [
        exportPlayer.displayName,
        exportSessionDate,
        viewMode === 'pass-chart' ? 'pass-chart' : 'pitch'
      ]
        .filter(Boolean)
        .map((s) => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase())
        .join('_')
      downloadFromUrl(url, `${safeName || 'pitch-export'}.png`)
    } finally {
      setExporting(false)
    }
  }, [
    canvasRef,
    exportDrillName,
    exportPlayer,
    exportSessionDate,
    exportSessionName,
    exportTallies,
    metrics,
    selectedElementId,
    viewMode
  ])

  return (
    <ErrorBoundary useUIFallback fallbackTitle='Failed to load pitch map'>
      <Card className='gap-2 p-3 xl:h-full xl:min-h-0 xl:flex-1 xl:basis-0 xl:overflow-hidden'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <PlayCategorySelection
            categories={categories}
            isCategorySelected={isCategorySelected}
            toggleCategory={toggleCategory}
          />
          <div className='flex items-center gap-2'>
            {supportsPassChart && (
              <PitchViewToggle value={viewMode} onChange={setViewMode} />
            )}
            <Button
              variant='ghost'
              size='sm'
              className='print:hidden'
              disabled={exporting || !exportPlayer}
              onClick={handleExportPng}
              aria-label='Download pitch as PNG'
              title='Download PNG'
            >
              <Download className='h-4 w-4' />
            </Button>
          </div>
        </div>

        {emptyMessage ? (
          <div className='flex flex-1 items-center justify-center py-6'>
            <p className='text-muted-foreground text-sm'>{emptyMessage}</p>
          </div>
        ) : (
          <>
            <PitchMetricsRow metrics={metrics} />

            <div className='xl:flex xl:min-h-0 xl:flex-1 xl:items-center xl:justify-center'>
              {supportsPassChart && viewMode === 'pass-chart' ? (
                <PassChart
                  sport={
                    sport as
                      | typeof ReportingSport.AmericanFootball
                      | typeof ReportingSport.CanadianFootball
                  }
                  pitch={pitch.coordinates}
                  arrows={arrows}
                  boundsArrows={allArrows}
                  selectedElementId={selectedElementId}
                  onElementClick={onElementClick}
                  canvasElRef={canvasRef}
                  gridSpacingMeters={gridSpacingMeters}
                  className='xl:aspect-auto xl:h-full'
                />
              ) : (
                <PitchMap
                  pitch={pitch}
                  points={points}
                  arrows={arrows}
                  selectedElementId={selectedElementId}
                  canvasElRef={canvasRef}
                  onElementClick={onElementClick}
                  drillRegion={drillRegion}
                  className='xl:h-full'
                  renderAttackDirection={sport === 'soccer'}
                />
              )}
            </div>
          </>
        )}
      </Card>
    </ErrorBoundary>
  )
}
